import React, { FC, FormEventHandler, useEffect, useState } from 'react';

import { Text, Button, Container, ContainerProps } from '@lglp/components';
import { disableGlobalScroll } from '@lglp/utils';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { RGPD } from 'translations';

import {
  GTAG,
  FUNCTIONALITY,
  setOpened,
  setSettingsAndClose,
  setForceDisplay,
} from '../../../../actions/rgpd';
import {
  CheckIcon,
  CTAArrow,
  ShieldLock,
} from '../../../../components-v2/Icons';
import { ArrowOrientation } from '../../../../components-v2/Icons/Arrow';
import { LinkedComponent } from '../../../../components-v2/LinkedComponent';
import {
  getRgpdForceDisplay,
  getRgpdOpened,
  getRgpdOptions,
  getRgpdVisibility,
} from '../../../../selectors/rgpd';
import { CTAButton } from '../../../CTAButton';
import styles from './RGPDBanner.module.scss';

export type RGPDProvider = {
  code: string;
  descriptionKey: string;
  mandatory?: boolean;
};

const providers: RGPDProvider[] = [
  {
    code: FUNCTIONALITY,
    descriptionKey: RGPD.RGPD_SETTINGS_OPERATION_COOKIES,
    mandatory: true,
  },
  { code: GTAG, descriptionKey: RGPD.RGPD_SETTINGS_GOOGLE_COOKIES },
];

export const RGPDBanner: FC<ContainerProps> = ({ className, ...rest }) => {
  const { t } = useTranslation();
  const options = useSelector(getRgpdOptions);
  const visible = useSelector(getRgpdVisibility);
  const opened = useSelector(getRgpdOpened);
  const displayed = useSelector(getRgpdForceDisplay);
  const dispatch = useDispatch();
  const [hasMounted, setHasMounted] = useState(false);
  const [values, setValues] = useState(options);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    disableGlobalScroll(hasMounted && visible && displayed);

    return () => {
      disableGlobalScroll(false);
    };
  }, [hasMounted, visible, displayed]);

  const onAll = async (accepted: boolean) => {
    const settings = {
      ...options,
      ...providers.reduce(
        (acc, p) => ({ ...acc, ...{ [p.code]: p.mandatory || accepted } }),
        {}
      ),
    };
    dispatch(setSettingsAndClose(settings));
  };

  const handleFormSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    dispatch(setSettingsAndClose({ ...options, ...values }));
  };

  if (!hasMounted || !visible || !displayed) {
    return null;
  }

  return (
    <Container className={styles.background}>
      <Container className={styles.container}>
        {!opened ? (
          <Container className={styles.mainContent}>
            <Container className={styles.intro}>
              <ShieldLock className={styles.icon} />
              <Container className={styles.question}>
                <Text
                  className={styles.text}
                  body={t(RGPD.RGPD_MAIN_DESCRIPTION)}
                />
                <LinkedComponent
                  className={[styles.link, styles.linkCookiesPolitic]}
                  link={{
                    url: t(RGPD.RGPD_MAIN_MORE_LINK),
                    linkType: 'link-web',
                  }}
                  onClick={() => {
                    dispatch(setForceDisplay(false));
                  }}
                >
                  {t(RGPD.RGPD_MAIN_MORE_TEXT)}
                </LinkedComponent>
              </Container>
            </Container>
            <Container className={styles.buttons}>
              <CTAButton
                cta={{ label: t(RGPD.RGPD_MAIN_YES_ALL), link: undefined }}
                onClick={() => {
                  onAll(true);
                }}
                className={styles.buttonOk}
              />
              <CTAButton
                cta={{ label: t(RGPD.RGPD_MAIN_NO_ALL), link: undefined }}
                onClick={() => {
                  onAll(false);
                }}
                className={styles.buttonNok}
              />
              <Button
                className={[styles.link, styles.prefLink]}
                onClick={() => {
                  dispatch(setOpened(true));
                }}
              >
                {t(RGPD.RGPD_MAIN_SHOW_SETTINGS)}
              </Button>
            </Container>
          </Container>
        ) : (
          <form
            className={styles.managePrefContent}
            onSubmit={handleFormSubmit}
          >
            <Button
              className={styles.backLink}
              onClick={() => {
                dispatch(setOpened(false));
              }}
            >
              <CTAArrow orientation={ArrowOrientation.LEFT} />
              <Button.Label>{t(RGPD.RGPD_SETTINGS_BACK)}</Button.Label>
            </Button>
            <Container className={styles.title}>
              <ShieldLock className={styles.icon} />
              <Text
                className={styles.textTitle}
                body={t(RGPD.RGPD_SETTINGS_TITLE)}
              />
            </Container>
            <Container className={styles.cookiesCheck}>
              {providers.map((provider, i) => (
                <Container key={i} className={styles.cookie}>
                  <input
                    type="checkbox"
                    className={styles.input}
                    id={provider.code}
                    disabled={provider.mandatory}
                    defaultChecked={options[provider.code]}
                    onChange={(event) => {
                      setValues({
                        ...values,
                        [provider.code]: event.target.checked,
                      });
                    }}
                  />
                  <label htmlFor={provider.code} className={styles.label}>
                    <Text
                      body={t(provider.descriptionKey)}
                      className={styles.cookieText}
                    />
                    <Container as={'span'} className={styles.toggleAndText}>
                      <Container as={'span'} className={styles.toggle}>
                        <CheckIcon />
                      </Container>
                      {provider.mandatory && (
                        <Text
                          body={t(RGPD.RGPD_SETTINGS_MANDATORY)}
                          className={styles.mandatoryText}
                        />
                      )}
                    </Container>
                  </label>
                </Container>
              ))}
            </Container>
            <CTAButton
              buttonType="submit"
              cta={{ label: t(RGPD.RGPD_SETTINGS_SAVE), link: undefined }}
              className={styles.saveButton}
            />
          </form>
        )}
      </Container>
    </Container>
  );
};
